import {Box, Grid, Theme, Typography} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import React, {FC, memo, useCallback, useEffect, useRef, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {useSelector} from 'react-redux';
import product3IllustrationImg from '../../../../assets/products/product-3/product3Illustration.png';
import {localized, localizedInterpolation} from '../../../../i18n/i18n';
import {AppState} from '../../../../state/store';
import {getConfigurationParamByName} from '../../../../utilities/helpers/product-helper';
import {BinaryButtonInput} from '../../../components/custom-components/configuration/dynamic-components/binary-button-input';
import {ImageRow} from '../../../components/custom-components/configuration/dynamic-components/image-row';
import {RecommondetionTextRow} from '../../../components/custom-components/configuration/dynamic-components/recommondetion-text-row';
import {IntroductionRow} from '../../../components/custom-components/configuration/step-2/introduction-row';
import {SargSelector} from '../../../components/custom-components/configuration/step-2/sarg-selector';
import {frameDepth, frameLength, hasFascia, numberOfTableFrameFittings} from '../configuration-constants';
import { validUnitsIds } from '../../../../utilities/helpers/culture-settings-helper';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    grid: {
      width: '686px',
    },
    gridItem: {
      height: '120px',
    },
    gridITexttem: {
      height: '80px',
    },
  }),
);

export interface PropsFromParent {}

export const Product3Step2: FC<PropsFromParent> = memo(() => {
  const classes = useStyles();
  const selectedProduct = useSelector((store: AppState) => store.projectReducer.selectedProduct);
  const {unitsId} = useSelector((store: AppState) => store.urlSettingsReducer.cultureUrlParameters);
  const {getValues, setValue} = useFormContext();
  const [hasNotSargSelected, setHasNotSargSelected] = useState<boolean>(getValues(hasFascia) === 'false' ?? false);
  const myRef = useRef<null | HTMLDivElement>(null);

  useEffect(() => {
    if (myRef && myRef.current) {
      myRef.current.scrollIntoView({block: 'end'});
    }
  }, []);

  const useCaseBtnCallback = useCallback(
    (value: boolean) => {
      setHasNotSargSelected(value);
      if (value) {
        setValue(frameLength, null);
        setValue(frameDepth, null);
        setValue(numberOfTableFrameFittings, null);
      } else {
        setValue(frameLength, undefined);
        setValue(frameDepth, undefined);
        setValue(numberOfTableFrameFittings, undefined);
      }
    },
    [setValue],
  );

  const NoTabelFrameWarning = useCallback(() => {
    return (
      <React.Fragment>
        <Grid item xs={12}>
          <Typography variant="body3" color="error">
            {localized('NoTableFrameWarning')}
          </Typography>
        </Grid>
      </React.Fragment>
    );
  }, []);

  return (
    <>
      <Box display="flex" justifyContent="center" alignItems="center" sx={{flexGrow: 1}}>
        <Grid container spacing={2} className={classes.grid}>
          <Grid ref={myRef} container spacing={3}>
            <IntroductionRow minHeight={'70'} />
          </Grid>

          <Grid container spacing={3}>
            <BinaryButtonInput
              formName={hasFascia}
              value1={'true'}
              value2={'false'}
              btnLabel1={localized('Yes')}
              btnLabel2={localized('No')}
              defaultValue={getConfigurationParamByName(selectedProduct, hasFascia)}
              label={localized('HasTableFrame')}
              buttonPressedCallback={useCaseBtnCallback}
            />
          </Grid>

          {!hasNotSargSelected ? (
            <>
              <Grid container spacing={3}>
                <ImageRow imgString={product3IllustrationImg} />
              </Grid>

              <Grid container spacing={3} className={classes.gridItem}>
                <RecommondetionTextRow
                  recommondetionString={localizedInterpolation('TableFrameMountingSuggestionX', {
                    value1: unitsId === validUnitsIds.iu ? `39.3 ${localized('Inches')}` : localized('Meter'),
                  })}
                />
              </Grid>

              <SargSelector productWithMotor={true} />
            </>
          ) : (
            <Grid container spacing={3} className={classes.gridItem}>
              <NoTabelFrameWarning></NoTabelFrameWarning>
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  );
});
